import React, { useEffect, useState } from "react";
import { MdErrorOutline, MdWarningAmber } from "react-icons/md";

const BRAND = "#004c50";

// Two-step account deletion.
//   Step 1 — intent modal. This is the ONE mobile card/modal exception: a
//            centered card on both mobile and desktop. Circle-exclamation
//            (muted red) top-left, "Delete account" title, then Stay / Delete.
//   Step 2 — final confirmation. On mobile this is a full SCREEN (a small modal
//            would be buried once the keyboard opens); on desktop it stays a
//            centered modal. Red warning triangle, then a "Type DELETE"
//            (case-insensitive) gate on the permanent-delete button.
const DeleteAccountModal = ({ isOpen, handleDeleteCard, handleDeleteCancel }) => {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState("");

  // Every (re)open starts fresh at step 1 with an empty field.
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setConfirmText("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canDelete = confirmText.trim().toLowerCase() === "delete";

  const close = () => {
    setStep(1);
    setConfirmText("");
    handleDeleteCancel();
  };

  // ---------- Step 1: intent ----------
  if (step === 1) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/60" onClick={close} />
        <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
          {/* Warning icon top-left, aligned with the title (no QUSHi badge). */}
          <div className="flex items-start gap-3">
            <MdErrorOutline className="mt-0.5 shrink-0 text-red-400" size={26} />
            <h2 className="text-title-subpage font-bold text-[#004d4d]">
              Delete account
            </h2>
          </div>
          <p className="mt-3 text-left text-sm leading-relaxed text-gray-600">
            We hate to see you go, but this is your call. Deleting your account
            is permanent and removes your profile, posts, and main QUSHi record.
            Some records may be kept where required by law or platform integrity.
          </p>
          <div className="mt-6 flex gap-4">
            <button
              type="button"
              onClick={close}
              className="h-12 flex-1 rounded-lg border bg-white font-semibold"
              style={{ borderColor: BRAND, color: BRAND }}
            >
              Stay on QUSHi
            </button>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="h-12 flex-1 rounded-lg bg-red-600 font-semibold text-white hover:bg-red-700"
            >
              Delete account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Step 2: final confirmation ----------
  return (
    <div className="fixed inset-0 z-50 md:flex md:items-center md:justify-center md:p-4">
      {/* Desktop backdrop only — on mobile this panel is a full screen. */}
      <div
        className="absolute inset-0 hidden bg-black/60 md:block"
        onClick={close}
      />
      <div className="relative z-10 flex h-full w-full flex-col bg-white p-6 md:h-auto md:max-w-md md:rounded-2xl md:shadow-xl">
        {/* Red warning triangle — same size / alignment as step 1's icon. */}
        <div className="flex items-start gap-3">
          <MdWarningAmber className="mt-0.5 shrink-0 text-red-600" size={26} />
          <h2 className="text-title-subpage font-bold text-[#004d4d]">
            Final confirmation
          </h2>
        </div>
        <p className="mt-3 text-left text-sm leading-relaxed text-gray-600">
          This permanently deletes your account and cannot be undone. Type
          DELETE to confirm.
        </p>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type DELETE"
          className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#004c50] focus:outline-none focus:ring-1 focus:ring-[#004c50]"
        />
        <button
          type="button"
          disabled={!canDelete}
          onClick={handleDeleteCard}
          className={`mt-5 h-12 w-full rounded-lg font-semibold text-white transition-colors ${
            canDelete
              ? "bg-red-600 hover:bg-red-700"
              : "cursor-not-allowed bg-gray-300"
          }`}
        >
          Permanently delete account
        </button>
        <button
          type="button"
          onClick={close}
          className="mt-3 h-12 w-full rounded-lg border bg-white font-semibold"
          style={{ borderColor: BRAND, color: BRAND }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
